import { Alert, Button, Skeleton, Space, Typography } from 'antd';
import { FilePdfOutlined, PrinterOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import dayjs from 'dayjs';
import { feeApi, type PaymentMethod } from '@/api/fee';
import { useAuthStore } from '@/store/auth';
import { extractError } from '@/shared/lib/error-message';
import { downloadFile } from '@/shared/lib/download';

// TODO: replace with a real branding setting once the org-settings table lands.
const SCHOOL_NAME = 'Smart School System';
const SCHOOL_TAGLINE = 'Tomorrow Is Your Destiny · Sialkot, Pakistan';

const METHOD_LABEL: Record<PaymentMethod, string> = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank Transfer',
  ONLINE_TRANSFER: 'Online Transfer',
};

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function FeeReceiptPrintPage() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);

  const receiptQuery = useQuery({
    queryKey: ['fee', 'receipt', id],
    queryFn: () => feeApi.getReceipt(id),
    enabled: Number.isFinite(id) && !!token,
  });

  const ledgerQuery = useQuery({
    queryKey: ['fee', 'ledger', receiptQuery.data?.enrollmentId ?? 'none'],
    queryFn: () => feeApi.ledger(receiptQuery.data!.enrollmentId),
    enabled: receiptQuery.data?.enrollmentId != null,
  });

  // Auto-trigger the print dialog once both pieces have loaded — but only the first time.
  useEffect(() => {
    if (receiptQuery.data && ledgerQuery.data) {
      const t = window.setTimeout(() => window.print(), 250);
      return () => window.clearTimeout(t);
    }
  }, [receiptQuery.data, ledgerQuery.data]);

  if (!token) return <Navigate to="/login" replace />;

  if (receiptQuery.isLoading || ledgerQuery.isLoading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }
  if (receiptQuery.isError || !receiptQuery.data) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="Could not load receipt"
          description={extractError(receiptQuery.error)}
          action={<Button onClick={() => navigate(-1)}>Back</Button>}
        />
      </div>
    );
  }

  const receipt = receiptQuery.data;
  const ledger = ledgerQuery.data;
  const studentName = ledger ? `${ledger.firstName} ${ledger.lastName}` : '—';
  const className = ledger ? `${ledger.className ?? '—'} · ${ledger.sectionName ?? '—'}` : '—';
  const regNo = ledger?.registrationNumber ?? '—';

  return (
    <div className="receipt-print-root">
      <PrintStyles />

      <div className="receipt-toolbar no-print">
        <Space>
          <Button onClick={() => navigate(-1)}>Close</Button>
          <Button
            icon={<FilePdfOutlined />}
            onClick={() =>
              downloadFile(`/reports/exports/fee-receipt/${id}.pdf`, {},
                `fee-receipt-${id}.pdf`)
            }
          >
            Save as PDF
          </Button>
          <Button type="primary" icon={<PrinterOutlined />} onClick={() => window.print()}>
            Print
          </Button>
        </Space>
      </div>

      <div className="receipt-sheet">
        <header className="receipt-header">
          <div className="receipt-brand">
            <img className="receipt-logo" src="/logo.png" alt={SCHOOL_NAME} />
            <div>
              <Typography.Text style={{ fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: '#666' }}>
                Fee Receipt
              </Typography.Text>
              <Typography.Title level={2} style={{ margin: '4px 0 2px', lineHeight: 1.1 }}>
                {SCHOOL_NAME}
              </Typography.Title>
              <Typography.Text type="secondary">{SCHOOL_TAGLINE}</Typography.Text>
            </div>
          </div>
          <div className="receipt-meta">
            <div className="receipt-number">
              <span className="label">Receipt #</span>
              <span className="value">{receipt.receiptNumber}</span>
            </div>
            <div>
              <span className="label">Date </span>
              <span>{dayjs(receipt.paymentDate).format('D MMM YYYY')}</span>
            </div>
          </div>
        </header>

        <div className="receipt-divider" />

        <section className="receipt-grid">
          <Field label="Student" value={studentName} />
          <Field label="Reg. #" value={regNo} />
          <Field label="Class · Section" value={className} />
          <Field label="Method" value={METHOD_LABEL[receipt.paymentMethod]} />
          {receipt.proofReference && <Field label="Reference" value={receipt.proofReference} />}
          {Number(receipt.advanceAmount) > 0 && (
            <Field label="Advance kept" value={`Rs ${PKR(receipt.advanceAmount)}`} />
          )}
        </section>

        <table className="receipt-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>Particulars</th>
              <th style={{ textAlign: 'right', width: 140 }}>Amount (Rs)</th>
            </tr>
          </thead>
          <tbody>
            {receipt.lines.map((ln, i) => (
              <tr key={`${ln.invoiceId}-${i}`}>
                <td>{i + 1}</td>
                <td>{ln.label}</td>
                <td style={{ textAlign: 'right' }}>{PKR(ln.amountPaid)}</td>
              </tr>
            ))}
            {Number(receipt.advanceAmount) > 0 && (
              <tr>
                <td>{receipt.lines.length + 1}</td>
                <td>Advance kept on account</td>
                <td style={{ textAlign: 'right' }}>{PKR(receipt.advanceAmount)}</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} style={{ textAlign: 'right', fontWeight: 600 }}>Total</td>
              <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 16 }}>Rs {PKR(receipt.totalAmount)}</td>
            </tr>
          </tfoot>
        </table>

        {receipt.reversed && (
          <div className="receipt-reversed">
            REVERSED{receipt.reversalReason ? ` · ${receipt.reversalReason}` : ''}
          </div>
        )}

        <footer className="receipt-footer">
          <div className="signature-block">
            <div className="signature-line" />
            <span>Cashier signature</span>
          </div>
          <div className="signature-block">
            <div className="signature-line" />
            <span>Parent / Guardian signature</span>
          </div>
        </footer>

        <div className="receipt-fineprint">
          Computer-generated. Keep this receipt safe — duplicates issued only against verified loss.
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="receipt-field">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
    </div>
  );
}

function PrintStyles() {
  return (
    <style>{`
      .receipt-print-root {
        background: #f5f5f5;
        min-height: 100vh;
        padding: 24px 16px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
      }
      .receipt-toolbar {
        width: min(720px, 100%);
        display: flex;
        justify-content: flex-end;
      }
      .receipt-sheet {
        width: min(720px, 100%);
        background: white;
        padding: 36px 40px 28px;
        box-shadow: 0 6px 24px rgba(0,0,0,0.06);
        border: 1px solid #e8e8e8;
        font-family: 'Helvetica Neue', Arial, sans-serif;
        color: #1f1f1f;
      }
      .receipt-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 24px;
      }
      .receipt-brand {
        display: flex;
        align-items: flex-start;
        gap: 14px;
      }
      .receipt-logo {
        width: 54px;
        height: auto;
        flex-shrink: 0;
        margin-top: 2px;
      }
      .receipt-meta {
        text-align: right;
        font-size: 13px;
      }
      .receipt-meta .label {
        color: #666;
        text-transform: uppercase;
        font-size: 10px;
        letter-spacing: 0.6px;
      }
      .receipt-meta .value {
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 22px;
        font-weight: 700;
        margin-left: 6px;
      }
      .receipt-divider {
        margin: 16px 0;
        border-top: 2px dashed #cfcfcf;
      }
      .receipt-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 8px 24px;
        margin-bottom: 20px;
      }
      .receipt-field {
        display: grid;
        grid-template-columns: 130px 1fr;
        font-size: 13px;
      }
      .receipt-field .label {
        color: #666;
        text-transform: uppercase;
        font-size: 10px;
        letter-spacing: 0.6px;
        align-self: center;
      }
      .receipt-field .value {
        font-weight: 600;
      }
      .receipt-table {
        width: 100%;
        border-collapse: collapse;
        margin: 14px 0 22px;
        font-size: 13px;
      }
      .receipt-table th {
        background: #f6f6f6;
        text-align: left;
        padding: 10px 12px;
        border-bottom: 1px solid #d9d9d9;
        font-weight: 600;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      .receipt-table td {
        padding: 10px 12px;
        border-bottom: 1px solid #f0f0f0;
      }
      .receipt-table tfoot td {
        border-top: 2px solid #1f1f1f;
        border-bottom: none;
        padding-top: 14px;
      }
      .receipt-reversed {
        margin: 16px 0;
        padding: 10px 14px;
        border: 2px solid #ff4d4f;
        color: #ff4d4f;
        font-weight: 700;
        text-align: center;
        letter-spacing: 1.5px;
        text-transform: uppercase;
      }
      .receipt-footer {
        margin-top: 36px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 32px;
      }
      .signature-block {
        font-size: 11px;
        color: #666;
        text-transform: uppercase;
        letter-spacing: 0.6px;
      }
      .signature-line {
        border-top: 1px solid #1f1f1f;
        margin-bottom: 6px;
        margin-top: 36px;
      }
      .receipt-fineprint {
        margin-top: 18px;
        font-size: 10px;
        color: #999;
        text-align: center;
      }

      @media print {
        body { background: white !important; }
        .no-print { display: none !important; }
        .receipt-print-root { padding: 0; background: white; }
        .receipt-sheet { box-shadow: none; border: none; padding: 12mm 14mm; }
        @page { size: A5; margin: 0; }
      }
    `}</style>
  );
}
